/** The section inspector, page builder and theme settings panels. Conventions: see `common.ts`. */
const sectionEditor: Record<string, string> = {
  // >>> inspector
  'inspector.common.heading': 'Heading',
  'inspector.common.supporting_line': 'Supporting line',
  'inspector.common.button_label': 'Button label',
  'inspector.common.button_link': 'Button link',
  'inspector.common.per_row': 'Per row',
  'inspector.common.columns_across': '{columns} across',
  'inspector.common.height': 'Height',
  'inspector.common.width': 'Width',
  'inspector.common.image': 'Image',
  'inspector.common.text': 'Text',
  'inspector.common.name': 'Name',
  'inspector.common.optional': 'Optional',
  'inspector.common.layout': 'Layout',
  'inspector.common.left': 'Left',
  'inspector.common.centred': 'Centred',
  'inspector.common.right': 'Right',
  'inspector.common.ask_phone': 'Ask for a phone number',
  'inspector.common.your_own': 'Your own ({value})',
  'inspector.common.seconds_value': '{seconds} seconds',
  'inspector.common.count_value': '{count}',
  'inspector.padding.none': 'None',
  'inspector.padding.sm': 'Small',
  'inspector.padding.md': 'Medium',
  'inspector.padding.lg': 'Large',
  'inspector.padding.xl': 'Extra large',
  'inspector.background.default': 'Page background',
  'inspector.background.surface': 'Soft tint',
  'inspector.background.primary': 'Brand colour',
  'inspector.span.full': 'Full width',
  'inspector.span.two_thirds': 'Two thirds',
  'inspector.span.half': 'Half',
  'inspector.span.third': 'One third',
  'inspector.theme_choice': "The theme's own",
  'inspector.panel.filled': 'Filled box',
  'inspector.panel.outlined': 'Hairline box',
  'inspector.panel.bare': 'No box',
  'inspector.card.standard': 'Standard',
  'inspector.card.framed': 'Framed',
  'inspector.card.airy': 'Airy',
  'inspector.card.overlay': 'Name on the picture',
  'inspector.filter.chips': 'Chips above the grid',
  'inspector.filter.sidebar': 'Sidebar beside it',
  'inspector.filter.inline': 'A quiet line of links',
  'inspector.tile.card': 'Picture in a card',
  'inspector.tile.overlay': 'Name on the picture',
  'inspector.tile.bare': 'Picture on its own',
  'inspector.page_size.theme': 'The theme’s own (24)',
  'inspector.page_size.per_page': '{size} per page',
  'inspector.toggle.on': 'Show',
  'inspector.toggle.off': 'Hide',
  'inspector.height.sm': 'Short',
  'inspector.height.md': 'Medium',
  'inspector.height.lg': 'Tall',
  'inspector.height.full': 'Full screen',
  'inspector.width.full': 'Full width',
  'inspector.width.contained': 'Inside the page',
  'inspector.hero.no_image': 'No picture yet. A hero works without one - the heading stands on the section background.',
  'inspector.hero.image_number': 'Image {number}',
  'inspector.hero.remove_image': 'Remove image {number}',
  'inspector.hero.image_hint': 'Landscape works best - around 1600 by 600 pixels.',
  'inspector.hero.add_image': 'Add image',
  'inspector.reposition.open': 'Reposition',
  'inspector.reposition.title': 'Choose what stays in view',
  'inspector.reposition.hint':
    'Drag the picture until the important part - a face, a product - sits well in both shapes. Arrow keys move it too.',
  'inspector.reposition.on_laptop': 'On a laptop or computer',
  'inspector.reposition.on_phone': 'On a phone',
  'inspector.reposition.on_shop': 'On your shop',
  'inspector.reposition.frame': 'Picture {frame} - drag to move it',
  'inspector.reposition.position': '{x}% across, {y}% down',
  'inspector.reposition.zoom': 'Zoom in',
  'inspector.reposition.zoom_laptop': 'Zoom in on a laptop or computer',
  'inspector.reposition.zoom_phone': 'Zoom in on a phone',
  'inspector.reposition.centre': 'Centre it again',
  'inspector.hero.max_images': 'Up to {max} images in one hero.',
  'inspector.hero.cycle': 'Cycle through the images',
  'inspector.hero.cycle_hint': 'Off leaves shoppers the dots to move it themselves.',
  'inspector.hero.seconds_each': 'Seconds on each image',
  'inspector.hero.cta_label_placeholder': 'Shop now',
  'inspector.hero.layout_center': 'Text over the image',
  'inspector.hero.layout_split': 'Text beside the image',
  'inspector.hero.product_image': 'Product picture over the middle',
  'inspector.hero.product_size': 'Product size',
  'inspector.hero.eyebrow': 'Small line on top',
  'inspector.hero.cta_style': 'Button look',
  'inspector.hero.cta_style_choice.button': 'Button',
  'inspector.hero.cta_style_choice.link': 'Underlined link',
  'inspector.hero.image_fit': 'Picture',
  'inspector.hero.image_fit_choice.framed': 'Framed',
  'inspector.hero.image_fit_choice.flush': 'Fill its half to the edges',
  'inspector.hero.image_fit_hint': 'Filling to the edges needs a contained banner with a background colour.',
  'inspector.hero.product_size_choice.sm': 'Small',
  'inspector.hero.product_size_choice.md': 'Medium',
  'inspector.hero.product_size_choice.lg': 'Large',
  'inspector.hero.width_hint': 'Full width runs the banner to the edges of the screen.',
  'inspector.hero.dimming': 'Darken the picture',
  'inspector.hero.dimming_hint': 'Darkens the photo so the heading stays readable.',
  'inspector.hero.dimming_choice.none': 'Not at all',
  'inspector.hero.dimming_choice.light': 'A little',
  'inspector.hero.dimming_choice.medium': 'Some',
  'inspector.hero.dimming_choice.strong': 'A lot',
  'inspector.hero.secondary_label': 'Second button text',
  'inspector.hero.secondary_placeholder': 'Call us',
  'inspector.hero.secondary_link': 'Second button link',
  'inspector.hero.badges': 'Benefit labels',
  'inspector.hero.badge_number': 'Benefit label {number}',
  'inspector.hero.badge_placeholder': 'Free consultation',
  'inspector.hero.remove_badge': 'Remove benefit label {number}',
  'inspector.hero.add_badge': 'Add benefit label',
  'inspector.hero.max_badges': 'Up to {max} benefit labels in one hero.',
  'inspector.slideshow.width_hint': 'Full width runs the slides to the edges of the screen.',
  'inspector.slideshow.rotate': 'Rotate automatically',
  'inspector.slideshow.rotate_hint': 'Paused while you are designing, so you can work on one slide.',
  'inspector.slideshow.seconds_per_slide': 'Seconds per slide',
  'inspector.video.source': 'Video from',
  'inspector.video.source_upload': 'An uploaded file',
  'inspector.video.youtube_link': 'YouTube link',
  'inspector.video.youtube_error':
    'That is not a YouTube link. Copy it from the video’s address bar or its Share button.',
  'inspector.video.youtube_hint': 'A watch, share, embed or Shorts link - all of them work.',
  'inspector.video.preview_title': 'Video preview',
  'inspector.video.file': 'Video file',
  'inspector.video.file_hint': 'Replace it or remove it at any time; the rest of the section stays as it is.',
  'inspector.video.shape': 'Shape',
  'inspector.video.shape_hint':
    'The frame the player is drawn at. A video that is a different shape is fitted inside it rather than stretched.',
  'inspector.video.aspect.16_9': 'Widescreen (16:9)',
  'inspector.video.aspect.4_3': 'Classic (4:3)',
  'inspector.video.aspect.1_1': 'Square (1:1)',
  'inspector.video.aspect.9_16': 'Portrait (9:16)',
  'inspector.video.autoplay': 'Start playing on its own',
  'inspector.video.autoplay_hint': 'Browsers only allow this with the sound off, so it plays muted.',
  'inspector.video.loop': 'Play again when it ends',
  'inspector.video.muted': 'Start muted',
  'inspector.video.muted_hint_autoplay': 'Kept on while the video starts on its own.',
  'inspector.video.muted_hint': 'Shoppers can turn the sound on from the player.',
  'inspector.video.rounded': 'Rounded corners',
  'inspector.video.overlay_position': 'Words over the video',
  'inspector.video.overlay_hint': 'They step aside once a shopper presses play.',
  'inspector.video.overlay_position_choice.none': 'None',
  'inspector.video.overlay_position_choice.bottom_left': 'Bottom left',
  'inspector.video.overlay_position_choice.center': 'Centre',
  'inspector.video.overlay_position_choice.bottom_center': 'Bottom centre',
  'inspector.video.overlay_heading': 'Heading on the video',
  'inspector.video.overlay_subheading': 'Line under it',
  'inspector.video.overlay_cta_label': 'Button text',
  'inspector.video.overlay_cta_href': 'Button link',
  'inspector.social.preview_empty': 'No icons yet - paste an address into one of the links below.',
  'inspector.social.preview_count_one': 'Shoppers see {count} icon.',
  'inspector.social.preview_count_other': 'Shoppers see {count} icons.',
  'inspector.social.icon_shape': 'Icon shape',
  'inspector.social.icon_style.plain': 'Just the icon',
  'inspector.social.icon_style.outlined': 'Icon in a ring',
  'inspector.social.icon_style.filled': 'Icon on a solid circle',
  'inspector.social.icon_size': 'Icon size',
  'inspector.social.icon_size_hint':
    'Every size stays big enough to tap on a phone, and icons that do not fit wrap onto a second line rather than shrinking.',
  'inspector.social.icon_size_choice.sm': 'Small',
  'inspector.social.icon_size_choice.md': 'Medium',
  'inspector.social.icon_size_choice.lg': 'Large',
  'inspector.social.position': 'Position',
  'inspector.social.brand_colors': 'Use each network’s own colour',
  'inspector.social.brand_colors_hint':
    'Off, the icons take your shop’s colour. On, each one is drawn in the network’s - the look most people picture, and the loudest thing on the page.',
  'inspector.social.network': 'Network',
  'inspector.social.platform_custom': 'Other',
  'inspector.social.custom_name_hint':
    'What this link is called. Shoppers using a screen reader hear it, and it is how the link is listed here.',
  'inspector.social.profile_address': 'Profile address',
  'inspector.social.whatsapp_error':
    'That is not a link or a phone number. Paste a wa.me link, or type the number the shop uses on WhatsApp.',
  'inspector.social.url_error':
    'That is not a web address. Open your profile and copy the address out of the browser’s address bar.',
  'inspector.social.whatsapp_hint':
    'A wa.me link, or just the phone number with its country code - a number is turned into the link for you.',
  'inspector.social.url_hint': 'Leave it empty and this icon is not shown to shoppers.',
  'inspector.social.name_hint':
    'Optional. Worth setting only where the network is not the whole story - a second account, or a shop-only page.',
  'inspector.featured.show_from': 'Show products from',
  'inspector.featured.show_from_hint': 'Newest products first when no category is chosen.',
  'inspector.featured.all_categories': 'All categories',
  'inspector.featured.quantity': 'How many',
  'inspector.featured.layout': 'Arrange as',
  'inspector.featured.layout_grid': 'Grid',
  'inspector.featured.layout_carousel': 'Slider',
  'inspector.categories.tile_style': 'Tile look',
  'inspector.categories.tile_style_choice.photo': 'Photo fills the tile',
  'inspector.categories.tile_style_choice.stand': 'Picture on a light panel',
  'inspector.categories.heading_align': 'Title position',
  'inspector.categories.heading_align_choice.theme': 'As the theme places it',
  'inspector.categories.heading_align_choice.left': 'Left',
  'inspector.categories.heading_align_choice.center': 'Centred',
  'inspector.product_tabs.quantity': 'Products per tab',
  'inspector.recently_viewed.quantity': 'Most to show',
  'inspector.product_tabs.tab_style': 'Tab look',
  'inspector.product_tabs.tab_style_underline': 'Underlined',
  'inspector.product_tabs.tab_style_pills': 'Pills',
  'inspector.product_tabs.tab_style_circles': 'Round pictures',
  'inspector.product_tabs.cta_label_placeholder': 'View all',
  'inspector.product_tabs.label': 'Tab name',
  'inspector.product_tabs.source': 'Shows',
  'inspector.product_tabs.sources.newest': 'New arrivals',
  'inspector.product_tabs.sources.best_selling': 'Best sellers',
  'inspector.product_tabs.sources.on_sale': 'On sale',
  'inspector.product_tabs.sources.category': 'One category',
  'inspector.product_tabs.source_hint.newest': 'Your most recently added products first.',
  'inspector.product_tabs.source_hint.best_selling': 'The products that have sold the most units first. Products with no sales yet follow, newest first.',
  'inspector.product_tabs.source_hint.on_sale': 'Products with a compare-at price above their price. Hidden on your shop while nothing is marked down.',
  'inspector.product_tabs.source_hint.category': 'The newest products from the category you pick below.',
  'inspector.product_tabs.category': 'Category',
  'inspector.product_tabs.image': 'Round picture',
  'inspector.product_tabs.pick_category': 'Pick a category',
  'inspector.image_with_text.image_side': 'Image on the',
  'inspector.image_with_text.points': 'Points with icons',
  'inspector.image_with_text.no_points': 'No points yet. Add short reasons to choose you, each with an icon.',
  'inspector.image_with_text.point_number': 'Point {number}',
  'inspector.image_with_text.remove_point': 'Remove point {number}',
  'inspector.image_with_text.point_text': 'Point text',
  'inspector.image_with_text.point_icon': 'Icon',
  'inspector.image_with_text.point_show': 'Show this point',
  'inspector.image_with_text.add_point': 'Add point',
  'inspector.image_with_text.new_point': 'A reason to choose you',
  'inspector.image_with_text.max_points': 'Up to {max} points in one section.',
  'inspector.image_with_text.points_layout': 'Points stand',
  'inspector.image_with_text.points_layout_choice.below': 'Under the text',
  'inspector.image_with_text.points_layout_choice.column': 'In their own column',
  'inspector.banner.message': 'Message',
  'inspector.banner.link_label': 'Link label',
  'inspector.banner.link_label_placeholder': 'See the offer',
  'inspector.banner.link': 'Link',
  'inspector.text.alignment': 'Alignment',
  'inspector.link_directory.source': 'Links from',
  'inspector.link_directory.source_hint':
    'Your categories: each main category is a heading and its sub-categories are the links. Your own groups: add link groups above.',
  'inspector.link_directory.source_choice.categories': 'Your categories',
  'inspector.link_directory.source_choice.manual': 'Your own groups',
  'inspector.link_directory.separator': 'Between links',
  'inspector.link_directory.separator_choice.pipe': 'Bar ( | )',
  'inspector.link_directory.separator_choice.comma': 'Comma ( , )',
  'inspector.link_directory.separator_choice.dot': 'Dot ( · )',
  'inspector.link_directory.columns': 'Columns on wide screens',
  'inspector.link_directory.heading_style': 'Group headings',
  'inspector.link_directory.heading_style_choice.bold': 'Bold',
  'inspector.link_directory.heading_style_choice.muted': 'Soft',
  'inspector.link_directory.text_size': 'Text size',
  'inspector.link_directory.text_size_choice.xs': 'Small',
  'inspector.link_directory.text_size_choice.sm': 'Regular',
  'inspector.link_directory.group_heading': 'Group heading',
  'inspector.link_directory.links': 'Links',
  'inspector.link_directory.links_hint': 'Shown in a row after the heading. Each link goes to a page on your shop.',
  'inspector.link_directory.link_label': 'Link words',
  'inspector.link_directory.link_href': 'Page on your shop',
  'inspector.link_directory.link_href_error': 'Use a path on your shop that starts with /, like /categories/fruits.',
  'inspector.link_directory.add_link': 'Add link',
  'inspector.link_directory.remove_link': 'Remove link',
  'inspector.contact.heading_hint': 'Leave blank on a page that already has this as its title.',
  'inspector.contact.ask_comment': 'Ask for a comment',
  'inspector.contact.success_message': 'Message after sending',
  'inspector.contact.email_note': 'Messages are emailed to your business address. Change it under Settings.',
  'inspector.product_details.intro':
    'The pictures, price, options and buy button are laid out by your theme. Add sections above and below to say more about the product.',
  'inspector.product_details.related': 'Suggested products underneath',
  'inspector.product_details.related_hint': 'Similar products, and what else sold alongside.',
  'inspector.product_details.booking_panel': 'Booking card',
  'inspector.product_details.booking_panel_choice.theme': 'As the theme draws it',
  'inspector.product_details.booking_panel_choice.off': 'Off - keep the buy box',
  'inspector.product_details.booking_panel_choice.sidebar': 'Beside the service, in place of the buy box',
  'inspector.product_details.booking_panel_hint': 'Needs a bookings section somewhere in your theme; without one the buy box stays.',
  'inspector.product_list.heading_placeholder': 'All products',
  'inspector.product_list.heading_hint': 'A category being browsed always keeps its own name.',
  'inspector.product_list.per_row': 'Products per row',
  'inspector.product_list.per_page': 'Products per page',
  'inspector.product_list.category_filter': 'Category filter',
  'inspector.product_list.tiles': 'Product tiles',
  'inspector.product_list.search_link': 'Link to search and filters',
  'inspector.product_list.search_link_hint': 'A shopper who knows what they want goes straight there.',
  'inspector.category_list.heading_placeholder': 'Shop by category',
  'inspector.category_list.per_row': 'Categories per row',
  'inspector.category_list.tiles': 'Tiles',
  'inspector.search_results.heading_placeholder': 'Search',
  'inspector.search_results.per_row': 'Results per row',
  'inspector.search_results.tiles': 'Result tiles',
  'inspector.search_results.filters': 'Filters beside the results',
  'inspector.search_results.filters_hint':
    'Price, category and the rest. Without them the results take the whole width.',
  'inspector.search_results.filter_panel': 'Filters box',
  'inspector.cart.heading_placeholder': 'Your cart',
  'inspector.cart.empty_label': 'When the cart is empty',
  'inspector.cart.empty_placeholder': 'Your cart is empty.',
  'inspector.cart.totals_panel': 'Totals panel',
  'inspector.checkout.intro':
    'The fields, their order and their checks are fixed - an order that cannot be delivered or charged helps nobody. Everything around them is yours: put reassurance above the form, or your returns policy under it.',
  'inspector.checkout.heading_placeholder': 'Checkout',
  'inspector.checkout.summary_panel': 'Order summary panel',
  'inspector.customer.intro':
    'The form itself is fixed - it is the gate on a shopper’s order history. What is yours is the words over it, and whatever you stand above and below it.',
  'inspector.customer.login_heading_placeholder': 'Sign in',
  'inspector.customer.register_heading_placeholder': 'Create an account',
  'inspector.customer.intro_label': 'Line underneath',
  'inspector.customer.login_intro_placeholder': 'See your orders, track a delivery and check out faster.',
  'inspector.customer.register_intro_placeholder': 'Keep your addresses saved and every order in one place.',
  'inspector.account.intro':
    'A shopper’s own page - their orders, addresses and details. Sections you add here stand around all four of its tabs.',
  'inspector.account.heading_placeholder': 'My account',
  'inspector.blog_index.heading_placeholder': "The blog's own title",
  'inspector.blog_index.heading_hint': 'Leave this blank and each blog keeps its own name.',
  'inspector.blog_index.layout': 'Layout',
  'inspector.blog_index.layout_choice.theme': 'As the theme draws it',
  'inspector.blog_index.layout_choice.grid': 'Cards',
  'inspector.blog_index.layout_choice.featured_sidebar': 'Lead story with a sidebar',
  'inspector.blog_index.layout_choice.list': 'Rows',
  'inspector.countdown.intro':
    'A clock counting down to the end of your sale. Shoppers see it tick every second.',
  'inspector.countdown.ends_on': 'Sale ends on',
  'inspector.countdown.ends_at_time': 'At',
  'inspector.countdown.zone_hint': 'In your store’s timezone ({zone}).',
  'inspector.countdown.unset_hint': 'Until you set an end time, this section is hidden on your shop.',
  'inspector.countdown.clear': 'Clear the end time',
  'inspector.countdown.style': 'Style',
  'inspector.countdown.style_choice.banner': 'Full-width band',
  'inspector.countdown.style_choice.boxed': 'Framed box',
  'inspector.countdown.style_choice.inline': 'One line',
  'inspector.countdown.show_days': 'Show days',
  'inspector.countdown.expired_behavior': 'When time is up',
  'inspector.countdown.expired_choice.hide': 'Hide the section',
  'inspector.countdown.expired_choice.show_message': 'Show a message',
  'inspector.countdown.expired_message': 'Message after the sale',
  'inspector.countdown.image_layout': 'Pictures',
  'inspector.countdown.image_layout_choice.beside': 'One beside the clock',
  'inspector.countdown.image_layout_choice.flanking': 'One on each side of the clock',
  'inspector.countdown.left_image': 'Left picture',
  'inspector.countdown.right_image': 'Right picture',
  'inspector.blog_posts.intro':
    'Your newest published blog posts, read live. Hidden while there are none or while your blog is switched off.',
  'inspector.blog_posts.blog': 'Blog',
  'inspector.blog_posts.all_blogs': 'All blogs',
  'inspector.blog_posts.limit': 'Posts shown',
  'inspector.blog_posts.layout': 'Layout',
  'inspector.blog_posts.layout_choice.grid': 'Cards',
  'inspector.blog_posts.layout_choice.list': 'A list',
  'inspector.blog_posts.layout_choice.featured': 'One lead story',
  'inspector.blog_posts.layout_choice.lead_grid': 'Lead story with a grid',
  'inspector.blog_posts.show_image': 'Show cover pictures',
  'inspector.blog_posts.show_excerpt': 'Show summaries',
  'inspector.blog_posts.show_date': 'Show dates',
  'inspector.blog_posts.cta_label': 'Link to the blog',
  'inspector.blog_post.intro':
    'The post is your writing, set in this theme’s type. What you arrange here is what stands under it - a product grid, a signup box - which is where a reader becomes a shopper.',
  'inspector.not_found.intro':
    'Where a broken link lands. A shopper who got here still wants to buy something, so give them somewhere to go.',
  'inspector.not_found.heading_placeholder': 'Page not found',
  'inspector.not_found.body_label': 'What you tell them',
  'inspector.not_found.body_placeholder': 'The page you were looking for has moved or never existed.',
  'inspector.not_found.cta_label_placeholder': 'Back to the shop',
  'inspector.bookings.services': 'Services',
  'inspector.bookings.services_hint':
    'Pick which of your services this form offers. Their names, times and prices are edited on the Services page.',
  'inspector.bookings.show_duration': 'Show how long each takes',
  'inspector.bookings.show_prices': 'Show prices',
  'inspector.bookings.show_prices_hint': 'A service with no price set stays blank either way.',
  'inspector.bookings.hours': 'Opening hours',
  'inspector.bookings.hours_hint':
    "The times shoppers can book. Set in your store's timezone, which you change under Settings.",
  'inspector.bookings.weekday.monday': 'Monday',
  'inspector.bookings.weekday.tuesday': 'Tuesday',
  'inspector.bookings.weekday.wednesday': 'Wednesday',
  'inspector.bookings.weekday.thursday': 'Thursday',
  'inspector.bookings.weekday.friday': 'Friday',
  'inspector.bookings.weekday.saturday': 'Saturday',
  'inspector.bookings.weekday.sunday': 'Sunday',
  'inspector.bookings.opens': 'Opens',
  'inspector.bookings.closes': 'Closes',
  'inspector.bookings.store_hours_title': 'Your appointment hours',
  'inspector.bookings.store_hours_hint':
    'This section offers the same hours as the Services page. Change them there and every bookings section follows.',
  'inspector.bookings.edit_store_hours': 'Edit appointment hours',
  'inspector.bookings.custom_hours': 'Use different hours for this section',
  'inspector.bookings.custom_hours_hint':
    'Set a week just for this section, such as a bridal diary that opens on Sundays.',
  'inspector.bookings.closed': 'Closed',
  'inspector.bookings.hours_range': '{opens} – {closes}',
  'inspector.bookings.how': 'How you take bookings',
  'inspector.bookings.slot_interval': 'Minutes between times',
  'inspector.bookings.slot_interval_hint':
    "How far apart the offered times stand. Shorter than a service's own length is fine - it fills gaps a rigid grid would waste.",
  'inspector.bookings.capacity': 'How many at once',
  'inspector.bookings.capacity_hint': 'Chairs, rooms, or people who can see someone at the same time.',
  'inspector.bookings.buffer': 'Turnaround minutes',
  'inspector.bookings.buffer_hint': 'Cleaning down, writing notes. Kept free after every appointment.',
  'inspector.bookings.lead_time': 'Shortest notice, in hours',
  'inspector.bookings.lead_time_hint': 'Nothing sooner than this can be booked. Zero takes walk-ins.',
  'inspector.bookings.window': 'How far ahead, in days',
  'inspector.bookings.form': 'The form',
  'inspector.bookings.ask_notes': 'Ask what it is about',
  'inspector.bookings.notes_label': 'What to call that question',
  'inspector.bookings.success_message': 'Message after booking',
  'inspector.bookings.email_note':
    'Bookings arrive unconfirmed, and both you and the customer are emailed. Yours goes to your business address - change it under Settings.',
  // Bookings: one page or step by step, and the merchant's own questions.
  'inspector.bookings.layout_group': "Form style",
  'inspector.bookings.layout_group_hint': "One page asks everything at once. Step by step asks one thing at a time, with a summary of their choices beside it.",
  'inspector.bookings.form_layout': "Form style",
  'inspector.bookings.form_layout_choice.single': "One page",
  'inspector.bookings.form_layout_choice.steps': "Step by step",
  'inspector.bookings.steps': "Steps",
  'inspector.bookings.steps_hint': "Drag or use the arrows to change the order. Date & time always comes after the service and the person, and Review is always last.",
  'inspector.bookings.step_kind.service': "Service",
  'inspector.bookings.step_kind.person': "Who",
  'inspector.bookings.step_kind.datetime': "Date & time",
  'inspector.bookings.step_kind.details': "Your details",
  'inspector.bookings.step_kind.review': "Review",
  'inspector.bookings.step_label': "Name of the {name} step",
  'inspector.bookings.step_visible': "Show {name}",
  'inspector.bookings.step_note.service': "Skipped when you offer only one service.",
  'inspector.bookings.step_note.person': "Only shown when customers choose who they see.",
  'inspector.bookings.step_note.datetime': "Always asked - it is the appointment itself.",
  'inspector.bookings.step_note.details': "Always asked - it collects their name and email.",
  'inspector.bookings.step_note.review': "Always the last step when shown.",
  'inspector.bookings.show_progress': "Show progress",
  'inspector.bookings.show_summary': "Show a summary of their choices",
  'inspector.bookings.summary_position': "Summary position",
  'inspector.bookings.summary_position_hint': "On phones the summary is always a bar customers can open.",
  'inspector.bookings.summary_position_choice.side': "Beside the form",
  'inspector.bookings.summary_position_choice.bottom': "Under the form",
  'inspector.bookings.date_picker': "Date picker",
  'inspector.bookings.date_picker_choice.calendar': "Calendar",
  'inspector.bookings.date_picker_choice.strip': "Row of days",
  'inspector.bookings.date_picker_choice.weeks': "Two weeks",
  'inspector.bookings.questions': "Your questions",
  'inspector.bookings.questions_hint': "Asked with their name and email. Answers appear on the appointment in Bookings.",
  'inspector.bookings.question_label': "Question",
  'inspector.bookings.question_kind': "Answer type",
  'inspector.bookings.question_kind_choice.text': "Short answer",
  'inspector.bookings.question_kind_choice.textarea': "Long answer",
  'inspector.bookings.question_kind_choice.select': "Pick one",
  'inspector.bookings.question_kind_choice.checkbox': "Tick box",
  'inspector.bookings.question_options': "Choices, one per line",
  'inspector.bookings.question_options_empty': "Add at least one choice, or customers will have nothing to pick.",
  'inspector.bookings.question_required': "Must be answered",
  'inspector.bookings.question_visible': "Ask this question",
  'inspector.bookings.question_remove': "Remove {name}",
  'inspector.bookings.question_add': "Add a question",
  'inspector.bookings.question_new': "Your question",
  'inspector.bookings.question_untitled': "Untitled question",
  'inspector.bookings.questions_full': "A form can ask up to {max} questions.",
  'inspector.product_reviews.intro':
    'The reviews are written by your signed-in customers. Approve, hide and reply to them on the Reviews page; here you choose how they are shown. Shoppers only see this section on product pages.',
  'inspector.product_reviews.page_size': 'Reviews shown at first',
  'inspector.product_reviews.default_sort': 'Show first',
  'inspector.product_reviews.sort.newest': 'Newest',
  'inspector.product_reviews.sort.highest': 'Highest rated',
  'inspector.product_reviews.sort.lowest': 'Lowest rated',
  'inspector.product_reviews.sort.photos': 'With photos',
  'inspector.product_reviews.layout': 'Layout',
  'inspector.product_reviews.layout_choice.list': 'A list',
  'inspector.product_reviews.layout_choice.grid': 'A grid of cards',
  'inspector.product_reviews.show_breakdown': 'Show the rating summary',
  'inspector.product_reviews.show_photos': 'Show customers’ photos',
  'inspector.slide.image': 'Slide image',
  'inspector.feature.title': 'What you offer',
  'inspector.feature.body': 'A few more words',
  'inspector.feature.icon': 'Icon',
  'inspector.feature.icons.truck': 'Truck',
  'inspector.feature.icons.shield': 'Shield',
  'inspector.feature.icons.return': 'Return',
  'inspector.feature.icons.support': 'Support',
  'inspector.feature.icons.payment': 'Payment',
  'inspector.feature.icons.sparkles': 'Sparkles',
  'inspector.feature.icons.leaf': 'Leaf',
  'inspector.feature.icons.award': 'Award',
  'inspector.feature.icons.gift': 'Gift',
  'inspector.feature.icons.clock': 'Clock',
  'inspector.feature.icons.heart': 'Heart',
  'inspector.feature.icons.package': 'Package',
  'inspector.feature.stat': 'Big figure',
  'inspector.features.style': 'Style',
  'inspector.features.style_choice.icons': 'Icons',
  'inspector.features.style_choice.stats': 'Big figures',
  'inspector.testimonial.quote': 'What they said',
  'inspector.testimonial.quote_hint': 'In their words. Quotes you did not receive are not yours to publish.',
  'inspector.testimonial.author': 'Who said it',
  'inspector.testimonial.role': 'Where they are from',
  'inspector.testimonial.stars': 'Stars',
  'inspector.testimonial.no_rating': 'No rating',
  'inspector.testimonial.stars_value': '{stars} stars',
  'inspector.faq.question': 'Question',
  'inspector.faq.answer': 'Answer',
  'inspector.gallery.caption': 'Caption',
  'inspector.gallery.href': 'Links to',
  'inspector.gallery.style': 'Style',
  'inspector.gallery.style_choice.grid': 'Grid',
  'inspector.gallery.style_choice.strip': 'One row of squares',
  'inspector.gallery.full_bleed': 'Run edge to edge',
  'inspector.gallery.cta_label': 'Button text',
  'inspector.gallery.cta_href': 'Button goes to',
  'inspector.promo_grid.layout_choice.two_equal': '2 equal tiles',
  'inspector.promo_grid.layout_choice.three_equal': '3 equal tiles',
  'inspector.promo_grid.layout_choice.four_equal': '4 equal tiles',
  'inspector.promo_grid.layout_choice.one_large_two_small': '1 large, 2 small',
  'inspector.promo_grid.layout_choice.one_large_four_small': '1 large, 4 small',
  'inspector.promo_grid.layout_choice.two_large_three_small': '2 large, 3 small',
  'inspector.promo_grid.layout_choice.row': 'A row of narrow cards (up to 6)',
  'inspector.promo_grid.gap': 'Space between tiles',
  'inspector.promo_grid.gap_choice.none': 'None',
  'inspector.promo_grid.gap_choice.sm': 'Small',
  'inspector.promo_grid.gap_choice.md': 'Medium',
  'inspector.promo_grid.gap_choice.lg': 'Large',
  'inspector.promo_tile.eyebrow': 'Small line above',
  'inspector.promo_tile.href': 'Tile links to',
  'inspector.promo_tile.text_position': 'Where the words sit',
  'inspector.promo_tile.position.top_left': 'Top left',
  'inspector.promo_tile.position.top_center': 'Top centre',
  'inspector.promo_tile.position.top_right': 'Top right',
  'inspector.promo_tile.position.center_left': 'Middle left',
  'inspector.promo_tile.position.center': 'Middle',
  'inspector.promo_tile.position.center_right': 'Middle right',
  'inspector.promo_tile.position.bottom_left': 'Bottom left',
  'inspector.promo_tile.position.bottom_center': 'Bottom centre',
  'inspector.promo_tile.position.bottom_right': 'Bottom right',
  'inspector.promo_tile.text_color': 'Text colour',
  'inspector.promo_tile.dimming': 'Picture dimming',
  'inspector.promo_tile.background_color': 'Tile colour',
  'inspector.brand_strip.style': 'Style',
  'inspector.brand_strip.style_choice.row': 'Row',
  'inspector.brand_strip.style_choice.carousel': 'Sliding row',
  'inspector.brand_strip.style_choice.grid': 'Grid of boxes',
  'inspector.brand_strip.logo_height': 'Logo size',
  'inspector.brand_strip.size_choice.sm': 'Small',
  'inspector.brand_strip.size_choice.md': 'Medium',
  'inspector.brand_strip.size_choice.lg': 'Large',
  'inspector.brand_strip.grayscale': 'Grey until pointed at',
  'inspector.category_strip.max_items': 'Categories shown',
  'inspector.category_strip.icon_shape': 'Icon shape',
  'inspector.category_strip.icon_shape_choice.circle': 'Circle',
  'inspector.category_strip.icon_shape_choice.square': 'Square',
  'inspector.category_strip.icon_shape_choice.none': 'No icon',
  'inspector.category_strip.icon_size': 'Icon size',
  'inspector.category_strip.icon_size_choice.sm': 'Small',
  'inspector.category_strip.icon_size_choice.md': 'Medium',
  'inspector.category_strip.label_position': 'Name position',
  'inspector.category_strip.label_position_choice.below': 'Under the icon',
  'inspector.category_strip.label_position_choice.beside': 'Beside the icon',
  'inspector.category_strip.item_dividers': 'Lines between categories',
  'inspector.brand.image': 'Logo',
  'inspector.brand.name': 'Brand name',
  'inspector.brand.href': 'Links to',
  'inspector.brand.href_hint': 'Leave it empty to link to this brand in your catalog.',
  'inspector.lookbook.intro': 'Upload a styled photo, then click on it to tag your products. Shoppers tap a dot to see the product.',
  'inspector.lookbook.image': 'Photo',
  'inspector.lookbook.layout': 'Layout',
  'inspector.lookbook.layout_choice.image_only': 'Photo only',
  'inspector.lookbook.layout_choice.image_with_list': 'Photo with product list',
  'inspector.lookbook.hotspot_style': 'Dot style',
  'inspector.lookbook.style_choice.dot': 'Dot',
  'inspector.lookbook.style_choice.plus': 'Plus',
  'inspector.lookbook.style_choice.number': 'Number',
  'inspector.lookbook.placer': 'Tag products',
  'inspector.lookbook.placer_hint': 'Click the photo to add a dot (up to {max}). Drag a dot, or select it and use the arrow keys, to move it.',
  'inspector.lookbook.placer_full': 'All {max} dots are used. Remove one to add another.',
  'inspector.lookbook.no_image': 'Upload a photo first - the dots are placed on it.',
  'inspector.lookbook.add': 'Add a dot',
  'inspector.lookbook.hotspot_name': 'Dot {number}: {name}',
  'inspector.lookbook.no_product': 'no product yet',
  'inspector.lookbook.remove': 'Remove this dot',
  'inspector.hotspot.product': 'Product',
  'inspector.hotspot.search': 'Search your products',
  'inspector.hotspot.change': 'Change',
  'inspector.hotspot.none': 'No product picked yet. The dot is not shown to customers until you pick one.',
  'inspector.hotspot.no_results': 'No products match.',
  'inspector.hotspot.label': 'Name on the card',
  'inspector.hotspot.label_placeholder': 'The product\'s own name',
  'inspector.hotspot.x': 'Across (%)',
  'inspector.hotspot.y': 'Down (%)',
  'inspector.before_after.intro': 'Upload two photos of the same thing, taken from the same spot. Customers drag the slider to compare them.',
  'inspector.before_after.before_image': 'Before photo',
  'inspector.before_after.after_image': 'After photo',
  'inspector.before_after.before_label': 'Before label',
  'inspector.before_after.after_label': 'After label',
  'inspector.before_after.orientation': 'Slider direction',
  'inspector.before_after.orientation_choice.horizontal': 'Side to side',
  'inspector.before_after.orientation_choice.vertical': 'Up and down',
  'inspector.before_after.start_position': 'Slider starts at (%)',
  'inspector.before_after.caption': 'Caption',
  'inspector.service.name': 'Service',
  'inspector.service.description': 'What it involves',
  'inspector.service.duration': 'How long it takes, in minutes',
  'inspector.service.duration_hint': 'This decides which times shoppers are offered, not just what the card says.',
  'inspector.service.price': 'Price',
  'inspector.service.price_placeholder': 'Leave blank to quote on consultation',
  'inspector.service.picture': 'Picture',
  'inspector.responsive.title': 'Phones and tablets',
  'inspector.responsive.hint':
    'Leave these alone and the section looks the same everywhere. Change one and only that screen changes.',
  'inspector.responsive.same_as_desktop': 'Same as desktop',
  'inspector.responsive.show_on.desktop': 'Show on desktop',
  'inspector.responsive.show_on.tablet': 'Show on tablet',
  'inspector.responsive.show_on.mobile': 'Show on mobile',
  'inspector.responsive.padding_tablet': 'Space above and below, on a tablet',
  'inspector.responsive.padding_mobile': 'Space above and below, on a phone',
  'inspector.block.fallback_name': 'Block',
  'inspector.block.position': 'One {name} of {total} in this section.',
  'inspector.block.show': 'Show this {name}',
  'inspector.block.hidden_hint': 'Hidden ones keep their content, ready for next time.',
  'inspector.delete_section': 'Delete this {name}',
  'inspector.more_options.show': 'More options',
  'inspector.more_options.hide': 'Fewer options',
  'inspector.layout.show_storefront': 'Show on the storefront',
  'inspector.layout.hidden_hint': 'Hidden sections keep their content, ready for next time.',
  'inspector.layout.padding': 'Space above and below',
  'inspector.layout.background': 'Background',
  'inspector.layout.section_width': 'Section width',
  'inspector.width.desktop': 'Desktop width',
  'inspector.width.mobile': 'Mobile width',
  'inspector.width.fit': 'Fit',
  'inspector.width.custom': 'Custom',
  'inspector.width.percent': '{label} in percent',
  'inspector.width.fit_hint': 'Fit keeps the width this section is designed for. Custom sets it yourself.',
  'inspector.layout.span_hint_full': 'Narrow this section to leave room for another one beside it.',
  'inspector.layout.span_hint_narrow':
    'Give the section above or below it a width too and they stand side by side. On its own it sits centred, and on a phone it always fills the screen.',
  // <<< inspector
  // >>> link-picker
  'link.none': 'Not linked yet',
  'link.choose': 'Choose where it goes',
  'link.change': 'Change',
  'link.remove': 'Remove the link',
  'link.where': 'Where it goes',
  'link.kind.home': 'Home page',
  'link.kind.all_products': 'All products',
  'link.kind.product': 'A product',
  'link.kind.collection': 'A category',
  'link.kind.page': 'One of your pages',
  'link.kind.post': 'A blog post',
  'link.kind.whatsapp': 'WhatsApp chat',
  'link.kind.phone': 'Phone call',
  'link.kind.email': 'Email',
  'link.kind.web': 'Another website',
  'link.chip.product': 'Product: {name}',
  'link.chip.collection': 'Category: {name}',
  'link.chip.page': 'Page: {name}',
  'link.chip.blog': 'Blog: {name}',
  'link.chip.post': 'Blog post: {name}',
  'link.chip.whatsapp': 'WhatsApp: {number}',
  'link.chip.phone': 'Call: {number}',
  'link.chip.email': 'Email: {address}',
  'link.chip.web': 'Website: {address}',
  'link.chip.custom': 'Link: {address}',
  'link.loading': 'Loading…',
  'link.search_products': 'Search your products',
  'link.no_products': 'You have no products on sale yet.',
  'link.no_products_match': 'No product matches that. Try a shorter word.',
  'link.no_categories': 'You have no categories yet.',
  'link.no_pages': 'You have no pages yet. Make one under Content.',
  'link.no_posts': 'You have no blog posts yet.',
  'link.input.whatsapp': 'WhatsApp number',
  'link.input.phone': 'Phone number',
  'link.input.email': 'Email address',
  'link.input.web': 'Website address',
  'link.input_hint.whatsapp': 'A 10-digit mobile number is enough - +91 is added for you.',
  'link.input_hint.phone': 'A 10-digit mobile number is enough - +91 is added for you.',
  'link.input_hint.email': 'Tapping the button opens the shopper’s email app, addressed to you.',
  'link.input_hint.web': 'Copy it from the other website’s address bar.',
  'link.error.whatsapp': 'That does not look like a phone number.',
  'link.error.phone': 'That does not look like a phone number.',
  'link.error.email': 'That does not look like an email address.',
  'link.error.web': 'That does not look like a website address.',
  'link.use': 'Use this',
  // <<< link-picker
  // >>> settings-content-upload
  'settings.layout.title': 'Layout',
  'settings.layout.family': 'Theme style',
  'settings.layout.advanced': 'Advanced: mix styles per area',
  'settings.layout.match_family': 'Match theme style',
  'settings.layout.area.header': 'Header',
  'settings.layout.area.footer': 'Footer',
  'settings.layout.area.product': 'Product page',
  'settings.layout.area.listing': 'Product listing',
  'settings.logo.title': 'Logo',
  'settings.logo.label': 'Logo',
  'settings.logo.hint':
    'Saved to your store, so it stays when you change theme. A transparent PNG or SVG-exported PNG looks best.',
  'settings.logo.width': 'Logo size',
  'settings.logo.size.sm': 'Small',
  'settings.logo.size.md': 'Medium',
  'settings.logo.size.lg': 'Large',
  'settings.logo.size.own': 'Your own size',
  'settings.logo.show_store_name': 'Show the store name beside the logo',
  'settings.logo.show_store_name_hint': 'Turn off when your logo already spells the name out.',
  'settings.logo.sticky': 'Keep the header visible while scrolling',
  'settings.favicon.label': 'Favicon',
  'settings.favicon.hint': 'The small icon in the browser tab. A square image, 32 pixels or larger.',
  'settings.banner.title': 'Announcement bar',
  'settings.banner.empty':
    'Nothing here yet. Add a banner for free shipping, a sale, or anything you want above every page.',
  'settings.banner.fallback_name': 'Banner {number}',
  'settings.banner.remove': 'Remove {name}',
  'settings.banner.message': 'Message',
  'settings.banner.message_placeholder': 'Free shipping on orders over ₹999',
  'settings.banner.link_label': 'Link label',
  'settings.banner.link_label_placeholder': 'Shop now',
  'settings.banner.link_label_hint': 'Leave both empty for a message with no link.',
  'settings.banner.href': 'Links to',
  'settings.banner.add': 'Add banner',
  'settings.banner.max': 'Up to {max} banners on one bar.',
  'settings.banner.autoplay': 'Rotate through the banners',
  'settings.banner.autoplay_hint': 'Off leaves shoppers the dots to move it themselves.',
  'settings.banner.interval': 'Seconds on each banner',
  'settings.appearance.title': 'Appearance',
  'settings.appearance.default': 'Theme default',
  'settings.appearance.custom': 'Custom colour',
  'settings.appearance.pick_custom': 'Pick a custom colour for {label}',
  'settings.appearance.background': 'Background colour',
  'settings.appearance.text': 'Text colour',
  'settings.appearance.low_contrast': 'Hard to read: the contrast is {ratio}:1. Aim for 4.5:1 or more.',
  'settings.appearance.divider': 'Divider thickness',
  'settings.appearance.divider_color': 'Divider colour',
  'settings.appearance.width': 'Bar width',
  'settings.appearance.width_page': 'Page',
  'settings.appearance.width_full': 'Full',
  'settings.appearance.padding': 'Padding',
  'settings.appearance.padding_top': 'Top',
  'settings.appearance.padding_bottom': 'Bottom',
  'settings.appearance.padding_left': 'Left',
  'settings.appearance.padding_right': 'Right',
  'settings.appearance.custom_padding': 'Exact padding',
  'settings.appearance.edges': 'Edges',
  'settings.appearance.edge_top': 'Top edge',
  'settings.appearance.edge_bottom': 'Bottom edge',
  'settings.appearance.edge_straight': 'Straight',
  'settings.appearance.edge_wave': 'Wave',
  'settings.appearance.edge_curve': 'Curve',
  'settings.appearance.auto': 'Auto',
  'settings.appearance.reset': 'Reset',
  'settings.appearance.reset_all': 'Reset to theme look',
  'settings.appearance.px_value': '{label} in pixels',
  'settings.header_look.title': 'Header',
  'settings.header_look.layout': 'Header style',
  'settings.header_look.layout_theme': 'The theme\'s own',
  'settings.header_look.layout_centered': 'Centred logo',
  'settings.header_look.location_selector': 'Delivery pincode box',
  'settings.header_look.category_icons': 'Category pictures in the header',
  'settings.header_look.cta_label': 'Header button',
  'settings.header_look.cta_label_hint':
    'A button at the end of the header on every page. Leave it empty for no button.',
  'settings.header_look.cta_label_placeholder': 'Book appointment',
  'settings.header_look.cta_href': 'Header button link',
  'settings.footer.title': 'Footer',
  'settings.footer.space_above': 'Extra space above',
  'settings.footer.space_below': 'Extra space below',
  'settings.footer.copyright': 'Copyright line',
  'settings.footer.copyright_size': 'Text size',
  'settings.footer.copyright_uppercase': 'Show in capital letters',
  'settings.footer.layout': 'Footer layout',
  'settings.footer.layout_theme': 'The theme\'s own',
  'settings.footer.layout_columns': 'Three columns',
  'settings.footer.show_social': 'Show social links',
  'settings.footer.social': 'Social links',
  'settings.footer.social_empty': 'No social links yet. Add the profiles your shop has.',
  'settings.footer.social_add': 'Add social link',
  'settings.footer.social_remove': 'Remove {name}',
  'settings.footer.social_max': 'Up to {max} social links.',
  'settings.footer.layout_large': 'Large',
  'settings.footer.large': 'Large footer',
  'settings.footer.background_image': 'Background photo',
  'settings.footer.overlay': 'Darken the photo',
  'settings.footer.bottom_bar': 'Bottom bar',
  'settings.footer.large_style': 'Look',
  'settings.footer.large_style_choice.standard': 'Standard',
  'settings.footer.large_style_choice.framed': 'Framed',
  'settings.footer.large_style_choice.contact': 'With contact details',
  'settings.footer.newsletter_heading': 'Signup heading',
  'settings.footer.newsletter_text': 'Signup text',
  'settings.footer.ribbon': 'Benefits strip',
  'settings.footer.ribbon_enabled': 'Show a scrolling strip above the footer',
  'settings.footer.directory': 'Link directory above the footer',
  'settings.footer.directory_choice.off': 'Off',
  'settings.footer.directory_choice.categories': 'Your categories',
  'settings.footer.directory_choice.manual': 'My own groups',
  'settings.footer.directory_title': 'Directory heading',
  'settings.footer.directory_groups': 'Link groups',
  'settings.footer.directory_empty': 'No groups yet. Add one per region, with a link for each city you deliver to.',
  'settings.footer.directory_group': 'Group {number}',
  'settings.footer.directory_add': 'Add group',
  'settings.footer.directory_up': 'Move group {number} up',
  'settings.footer.directory_down': 'Move group {number} down',
  'settings.footer.directory_remove': 'Remove group {number}',
  'settings.footer.apps': 'App download buttons',
  'settings.footer.play_store_url': 'Google Play link',
  'settings.footer.app_store_url': 'App Store link',
  'settings.footer.play_store_error': 'Use your app’s Google Play link, starting with https://play.google.com/',
  'settings.footer.app_store_error': 'Use your app’s App Store link, starting with https://apps.apple.com/',
  'settings.footer.ribbon_empty': 'No items yet. Add a short benefit, like Free delivery.',
  'settings.footer.ribbon_item_text': 'Item {number}',
  'settings.footer.ribbon_icon': 'Icon for item {number}',
  'settings.footer.ribbon_up': 'Move item {number} up',
  'settings.footer.ribbon_down': 'Move item {number} down',
  'settings.footer.ribbon_remove': 'Remove item {number}',
  'settings.footer.ribbon_add': 'Add item',
  'settings.footer.ribbon_max': 'Up to {max} items.',
  'settings.footer.ribbon_speed': 'Speed',
  'settings.header_menu.title': 'Header menu',
  'settings.header_menu.limit': 'Categories to show',
  'settings.header_menu.limit_hint':
    'Your top-level categories stand in the header row when you have not built a header menu of your own. Show fewer if the names run past the edge of the page and leave the row scrolling sideways.',
  'settings.header_menu.limit_theme': 'As many as the theme shows',
  'settings.header_menu.limit_count_one': '{count} category',
  'settings.header_menu.limit_count_other': '{count} categories',
  'settings.header_menu.mega_menu': 'Category menu style',
  'settings.header_menu.mega_menu_hint':
    'Big menus open when a shopper points at or taps the arrow next to a category in the header.',
  'settings.header_menu.mega_menu_off': 'Small list (standard)',
  'settings.header_menu.mega_menu_columns': 'Big menu with columns',
  'settings.header_menu.mega_menu_columns_with_promo': 'Big menu with columns and a picture',
  'settings.header_menu.promo_heading': 'Picture heading',
  'settings.header_menu.promo_heading_hint':
    'Leave empty to show "Shop all" and the category name.',
  'settings.header_menu.promo_image': 'Picture',
  'settings.header_menu.promo_image_hint':
    'Leave empty to use each category\'s own picture, or a block of your theme colour.',
  'settings.header_menu.promo_link': 'Picture link',
  'settings.header_menu.promo_link_hint': 'Leave empty to link to the category itself.',
  'settings.header_menu.mega_menu_columns_with_cards': 'Big menu with columns and two picture cards',
  'settings.header_menu.card_title': 'Picture card {number}',
  'settings.header_menu.card_heading': 'Caption',
  'settings.header_menu.card_heading_hint_first': 'Leave empty to show "Best Selling".',
  'settings.header_menu.card_heading_hint_second': 'Leave empty to show "New Arrivals".',
  'settings.header_menu.card_image': 'Picture',
  'settings.header_menu.card_image_hint': 'Leave empty to use a sub-category’s picture.',
  'settings.header_menu.card_link': 'Link',
  'settings.header_menu.card_link_hint': 'Leave empty to open the category.',
  'settings.department_bar.title': 'Department bar',
  'settings.department_bar.menu_label': 'Menu button label',
  'settings.department_bar.tagline': 'Tagline',
  'settings.department_bar.tagline_hint': 'Sits at the end of the bar. Leave empty to hide it.',
  'settings.colors.title': 'Colours',
  'settings.color.primary.label': 'Brand colour',
  'settings.color.primary.hint': 'Buttons, links and prices.',
  'settings.color.background.label': 'Page background',
  'settings.color.background.hint': 'Behind everything.',
  'settings.color.surface.label': 'Soft tint',
  'settings.color.surface.hint': 'Cards, image placeholders, tinted sections.',
  'settings.color.text.label': 'Text',
  'settings.color.text.hint': 'Headings and body copy.',
  'settings.color.muted.label': 'Quiet text',
  'settings.color.muted.hint': 'Captions and supporting lines.',
  'settings.color.accent.label': 'Accent',
  'settings.color.accent.hint': 'The theme\'s accent colour, or your brand colour where it has none.',
  'settings.type.title': 'Type',
  'settings.type.font': 'Font',
  'settings.type.font_hint': 'Body copy, and headings unless a heading font is chosen below.',
  'settings.type.heading_font': 'Heading font',
  'settings.type.heading_font_hint': 'A serif here over a sans body is the quickest way to look editorial.',
  'settings.type.same_as_body': 'Same as body',
  'settings.type.heading_weight': 'How bold headings are',
  'settings.type.weight.medium': 'Medium',
  'settings.type.weight.semibold': 'Semibold',
  'settings.type.weight.bold': 'Bold',
  'settings.type.weight.extra_bold': 'Extra bold',
  'settings.type.base_size': 'Body text size',
  'settings.buttons.title': 'Buttons',
  'settings.buttons.style': 'Style',
  'settings.buttons.style_solid': 'Filled',
  'settings.buttons.style_outline': 'Outlined',
  'settings.buttons.radius': 'Corner roundness',
  'settings.buttons.radius_hint': 'Also rounds cards and images.',
  'content.your_shop': 'Your shop',
  'content.area.header': 'Header',
  'content.area.footer': 'Footer',
  'content.area.global': 'Everywhere',
  'content.role.heading': 'Headings',
  'content.role.label': 'Labels',
  'content.role.button': 'Buttons',
  'content.role.placeholder': 'Placeholders',
  'content.role.message': 'Messages',
  'content.role.empty': 'Empty states',
  'content.role.validation': 'Validation',
  'content.role.promo': 'Promotions',
  'content.role.legal': 'Fine print',
  'content.find': 'Find text',
  'content.find_hint':
    'Looks through what your shop says today and what your theme ships, so you can search for the words you can see on the page.',
  'content.no_matches': 'Nothing here matches “{query}”.',
  'content.edited_one': '{count} edited',
  'content.edited_other': '{count} edited',
  'content.use_theme_words': 'Use the theme’s words',
  'content.reset_area': 'Reset {area}',
  'content.reset_all': 'Reset all text ({total})',
  'content.reset_all_confirm': 'Put every piece of text back to what your theme says?',
  'upload.make_with_ai': 'Make with AI',
  'upload.image_use_failed': 'Could not use that image.',
  'upload.adjust.open': 'Adjust',
  'upload.adjust.title': 'Adjust the picture',
  'upload.adjust.hint':
    'Drag the frame to choose what is kept, and zoom to fill it. Saving stores a new, cropped copy - the one your header, browser tab and emails use.',
  'upload.adjust.shape': 'Shape',
  'upload.adjust.zoom': 'Zoom',
  'upload.adjust.trim': 'Trim empty space',
  'upload.adjust.save': 'Save picture',
  'upload.adjust.reading': 'Reading the picture…',
  'upload.adjust.load_failed':
    'This picture could not be opened for adjusting. Upload it again from your device, then try.',
  'upload.adjust.failed': 'Could not save the adjusted picture.',
  'upload.adjust.nothing_to_trim': 'There is no empty border to trim.',
  'upload.adjust.trim_failed': 'Could not look for empty space in this picture.',
  'upload.image_failed': 'Could not upload that image.',
  'upload.video_failed': 'Could not upload that video.',
  'upload.uploading_image': 'Uploading image',
  'upload.uploading_video': 'Uploading video',
  'upload.uploading': 'Uploading…',
  'upload.uploading_resizing': 'Uploading and resizing…',
  'upload.replace': 'Replace',
  'upload.media_library': 'Media Library',
  'upload.select_from_library': 'Select from Media Library',
  'upload.upload_named': 'Upload {name}',
  'upload.or_drop': ' or drop it here',
  'upload.drop_hint': 'Or drag an image here.',
  'upload.image_formats': 'JPEG, PNG, WebP, GIF or AVIF, up to 5 MB. Saved as WebP.',
  'upload.video_formats': 'MP4, WebM or Ogg, up to {max} MB. Saved as uploaded.',
  'upload.video_unavailable': 'Videos cannot be uploaded here. A merchant adds their own after applying the theme.',
  'upload.choose_named': 'Choose {name}',
  'upload.product_photos': 'Choose from your product photos',
  'upload.product_photos_title': 'Your product photos',
  'upload.product_photos_search': 'Search by product name',
  'upload.product_photos_empty': 'None of your products has a photo yet.',
  'upload.product_photos_no_match': 'No product with a photo matches that.',
  'sheet.resize': 'Resize the editor panel',
  'sheet.resize_hint': 'Drag, or tap, to resize',
  // <<< settings-content-upload
  // >>> builder-parts-library
  'builder.disabled.before':
    'Your {page} is turned off, so shoppers never see it. You can still arrange it - it comes back exactly like this if you turn it on again under',
  'builder.disabled.link': 'Pages and features',
  'builder.disabled.after': '.',
  'builder.empty': 'Your {page} is empty. Add a section to start building it.',
  'builder.block': 'Block',
  'builder.collapse': 'Collapse {name}',
  'builder.expand': 'Expand {name}',
  'builder.row.this_page': 'this page',
  'builder.row.not_on': 'not on {devices}',
  'builder.span.full': 'full width',
  'builder.span.two_thirds': 'two thirds',
  'builder.span.half': 'half',
  'builder.span.third': 'one third',
  'builder.device.mobile': 'mobile',
  'builder.device.tablet': 'tablet',
  'builder.device.desktop': 'desktop',
  'builder.hide': 'Hide {name}',
  'builder.show': 'Show {name}',
  'builder.more_actions': 'More actions for {name}',
  'builder.duplicate': 'Duplicate',
  'builder.save_reusable': 'Save as reusable section',
  'builder.add_block': 'Add {block}',
  'builder.add_section': 'Add section',
  'builder.paste': 'Paste',
  'builder.paste_named': 'Paste {name}',
  'builder.full': 'A page holds up to {max} sections. Remove one to add another.',
  'builder.reset_confirm': 'Reset "{name}" to how it started? Its content is lost.',
  'builder.save_dialog.name': 'Name',
  'builder.save_dialog.hint': 'It will appear in Add section, on every page of this shop.',
  'tree.not_on': 'not on {devices}',
  'tree.hide': 'Hide {name}',
  'tree.show': 'Show {name}',
  'tree.more_actions': 'More actions for {name}',
  'tree.duplicate': 'Duplicate',
  'tree.add_to': 'Add to {region}',
  'tree.full': 'A page holds up to {max} components. Remove one to add another.',
  'tree.reset_confirm': 'Reset "{name}" to how it started? Its settings are lost.',
  'library.add_to': 'Add to {region}',
  'library.search_components': 'Search components',
  'library.search_components_placeholder': 'Search components…',
  'library.parts_no_match': 'Nothing here matches “{query}”. Try a shorter word - “price”, “button”, “text”.',
  'library.parts_all_present': 'This page already has one of everything your theme offers here.',
  'library.goes_in': 'Goes in {region}',
  'library.add_section': 'Add a section',
  'library.this_theme': 'This theme',
  'library.saved_shelf': 'Your saved sections',
  'library.saved_description': 'Saved from one of your pages.',
  'library.search_sections': 'Search sections',
  'library.search_sections_placeholder': 'Search sections…',
  'library.sections_no_match': 'Nothing here matches “{query}”. Try a shorter word - “product”, “banner”, “text”.',
  'library.forget': 'Forget {name}',
  'sortable.drag': 'Drag to reorder',
  'sortable.move_up': 'Move {name} up',
  'sortable.move_down': 'Move {name} down',
  'sortable.move_up_within': 'Move {name} up in {within}',
  'sortable.move_down_within': 'Move {name} down in {within}',
  'part_inspector.theme_own': 'The theme\'s own',
  'part_inspector.align.left': 'Left',
  'part_inspector.align.center': 'Centred',
  'part_inspector.align.right': 'Right',
  'part_inspector.space.none': 'None',
  'part_inspector.space.xs': 'Tiny',
  'part_inspector.space.sm': 'Small',
  'part_inspector.space.md': 'Medium',
  'part_inspector.space.lg': 'Large',
  'part_inspector.space.xl': 'Extra large',
  'part_inspector.size.xs': 'Tiny',
  'part_inspector.size.sm': 'Small',
  'part_inspector.size.base': 'Normal',
  'part_inspector.size.lg': 'Large',
  'part_inspector.size.xl': 'Extra large',
  'part_inspector.size.2xl': 'Heading',
  'part_inspector.size.3xl': 'Big heading',
  'part_inspector.size.4xl': 'Huge',
  'part_inspector.size.5xl': 'Enormous',
  'part_inspector.weight.normal': 'Normal',
  'part_inspector.weight.medium': 'Medium',
  'part_inspector.weight.semibold': 'Semi-bold',
  'part_inspector.weight.bold': 'Bold',
  'part_inspector.case.none': 'As written',
  'part_inspector.case.uppercase': 'UPPERCASE',
  'part_inspector.case.capitalize': 'Title Case',
  'part_inspector.background.none': 'None',
  'part_inspector.background.surface': 'Soft panel',
  'part_inspector.background.primary': 'Brand colour',
  'part_inspector.width.auto': 'As wide as its content',
  'part_inspector.width.full': 'The whole column',
  'part_inspector.device.desktop': 'Desktop',
  'part_inspector.device.tablet': 'Tablet',
  'part_inspector.device.mobile': 'Mobile',
  'part_inspector.color.ink': 'Body text',
  'part_inspector.color.muted': 'Muted text',
  'part_inspector.color.primary': 'Brand colour',
  'part_inspector.color.background': 'Page background',
  'part_inspector.color.custom': 'A colour of my own',
  'part_inspector.this_page': 'This page',
  'part_inspector.remove': 'Remove {name}',
  'part_inspector.group.content': 'Content',
  'part_inspector.group.placement': 'Placement',
  'part_inspector.group.style': 'Style',
  'part_inspector.words': 'Words',
  'part_inspector.words_hint': 'Blank keeps what your shop already says here.',
  'part_inspector.text': 'Text',
  'part_inspector.stands_in': 'Stands in',
  'part_inspector.required_note':
    'This page needs this, so it cannot be hidden or removed. You can still move it and change how it looks.',
  'part_inspector.show_on_storefront': 'Show on the storefront',
  'part_inspector.show_on_storefront_hint': 'Hidden components keep their settings, ready for next time.',
  'part_inspector.space_above': 'Space above',
  'part_inspector.space_below': 'Space below',
  'part_inspector.alignment': 'Alignment',
  'part_inspector.width_label': 'Width',
  'part_inspector.hide_on': 'Hide on',
  'part_inspector.hide_on_hint':
    'Left unticked it shows on every screen. Design the phone with the phone preview open.',
  'part_inspector.text_size': 'Size',
  'part_inspector.text_weight': 'Boldness',
  'part_inspector.text_case': 'Letter case',
  'part_inspector.color_label': 'Colour',
  'part_inspector.color_hint': 'Naming one of your shop\'s colours keeps this in step when you repaint.',
  'part_inspector.color_custom_label': 'Its colour',
  'part_inspector.background_label': 'Background',
  'part_inspector.background_hint': 'A background gives the component padding of its own.',
  'preview.empty_region': '{region} - nothing in it.',
  'preview.checkout_fields_note':
    'The address, delivery and payment fields. Their order and their rules are fixed.',
  // <<< builder-parts-library
  // >>> inline-edit
  'inline.on_page': '{field} (on the page)',
  'inline.field.heading': 'Heading',
  'inline.field.subheading': 'Supporting line',
  'inline.field.cta_label': 'Button text',
  'inline.field.body': 'Paragraph',
  'inline.field.text': 'Text',
  'inline.field.link_label': 'Link text',
  'inline.field.title': 'Section title',
  'inline.field.subtitle': 'Section subtitle',
  'inline.field.quote': 'Review',
  'inline.field.author': 'Customer name',
  'inline.field.role': 'Customer detail',
  'inline.field.question': 'Question',
  'inline.field.answer': 'Answer',
  'inline.field.caption': 'Caption',
  'inline.field.secondary_cta_label': 'Second button text',
  'inline.field.badges': 'Benefit label',
  'inline.field.stat': 'Big figure',
  'inline.field.date': 'When',
  'inline.hint': 'Click to type here. Enter saves, Esc cancels.',
  'inline.hint_multiline': 'Click to type here. Enter saves, Shift+Enter starts a new line, Esc cancels.',
  'inline.hint_rich':
    'Click to type here. Enter starts a new paragraph, Ctrl+B makes words bold, and clicking away saves. Esc cancels.',
  'inline.disabled_translation': 'Editing on the page is off while you translate. Use the fields in the panel.',
  // <<< inline-edit
  // >>> catalog pickers - which of the store's services / plans / FAQs a section shows
  'inspector.catalog.loading': 'Loading your list…',
  'inspector.catalog.load_failed': "Couldn't load the list. Reopen this section to try again.",
  'inspector.catalog.keep_ticked': 'Keep at least one ticked, or turn on "Show all".',
  'inspector.catalog.minutes': '{count} min',
  'inspector.catalog.all_services': 'Show all services',
  'inspector.catalog.all_services_hint': 'New services you add on the Services page appear here on their own.',
  'inspector.catalog.services_legend': 'Services to show',
  'inspector.catalog.no_services': 'No services yet. Add them on the Services page and they appear here.',
  'inspector.catalog.manage_services': 'Manage services',
  'inspector.catalog.all_plans': 'Show all plans',
  'inspector.catalog.all_plans_hint': 'New plans you add on the Pricing plans page appear here on their own.',
  'inspector.catalog.plans_legend': 'Plans to show',
  'inspector.catalog.no_plans': 'No plans yet. Add them on the Pricing plans page and they appear here.',
  'inspector.catalog.manage_plans': 'Manage plans',
  'inspector.catalog.all_faqs': 'Show all questions',
  'inspector.catalog.all_faqs_hint': 'New questions you add on the FAQs page appear here on their own.',
  'inspector.catalog.faqs_legend': 'Questions to show',
  'inspector.catalog.no_faqs': 'No questions yet. Add them on the FAQs page and they appear here.',
  'inspector.catalog.manage_faqs': 'Manage FAQs',
  // <<< catalog pickers
  'inspector.pricing_plans.intro': 'Show two to four of your plans side by side. Their names, prices and what each includes are edited on the Pricing plans page.',
  'inspector.pricing_plans.layout': 'Layout',
  'inspector.pricing_plans.layout_choice.cards': 'Plans under the heading',
  'inspector.pricing_plans.layout_choice.intro_left': 'Heading beside the plans',
  'inspector.pricing_plans.card_style': 'Card style',
  'inspector.pricing_plans.card_style_choice.outlined': 'Outlined',
  'inspector.pricing_plans.card_style_choice.filled': 'Filled',
  'inspector.pricing_plans.card_style_choice.shadow': 'Shadow',
  'inspector.pricing_plans.card_style_choice.tinted': 'Tinted',
  'inspector.pricing_plans.eyebrow': 'Small line on top',
  'inspector.pricing_plans.body': 'Paragraph',
  'inspector.pricing_plans.cta_label': 'Button text',
  'inspector.pricing_plans.cta_href': 'Button goes to',
  'inspector.pricing_plan.name': 'Plan name',
  'inspector.pricing_plan.price': 'Price',
  'inspector.pricing_plan.period': 'Per',
  'inspector.pricing_plan.description': 'Short description',
  'inspector.pricing_plan.features': 'What it includes',
  'inspector.pricing_plan.features_hint': 'One point per line.',
  'inspector.pricing_plan.cta_label': 'Button text',
  'inspector.pricing_plan.href': 'Button goes to',
  'inspector.pricing_plan.href_booking_hint': 'Leave empty to open your Book page on the service.',
  'inspector.pricing_plan.service': 'Books this service',
  'inspector.pricing_plan.service_none': 'None - a plain link',
  'inspector.pricing_plan.service_hint': 'The button opens the booking form with this service already chosen.',
  'inspector.pricing_plan.service_missing': 'This service is no longer in your booking form. Pick another, or add it back to the booking form.',
  'inspector.pricing_plan.service_price_differs': 'Your booking form charges {price} for this service. Change the price above or in the booking form so they match.',
  'inspector.pricing_plan.href_product_hint': 'Not used while a product is linked - the button opens that product.',
  'inspector.pricing_plan.product': 'Buys this product',
  'inspector.pricing_plan.product_none': 'No product linked. The button goes where “Button goes to” says.',
  'inspector.pricing_plan.product_clear': 'Remove',
  'inspector.pricing_plan.product_price_differs': 'This product sells for {price}. Change the price above or the product’s price so they match.',
  'inspector.pricing_plan.badge': 'Tag',
  'inspector.pricing_plan.featured': 'Highlight this plan',
  'inspector.pricing_plan.note': 'Line under the button',
  'inspector.product_spotlight.intro': 'Show one product large, with its price and an add-to-cart button. Until you pick one, your newest product is shown.',
  'inspector.product_spotlight.none': 'No product picked yet. Your newest product is shown until you pick one.',
  'inspector.product_spotlight.eyebrow': 'Small line above the name',
  'inspector.product_spotlight.image': 'Bigger picture',
  'inspector.product_spotlight.image_side': 'Picture side',
  'inspector.product_spotlight.image_side_choice.left': 'Left',
  'inspector.product_spotlight.image_side_choice.right': 'Right',
  'inspector.product_spotlight.show_description': 'Show the short description',
  'inspector.before_after.layout': 'Comparisons',
  'inspector.before_after.layout_choice.single': 'One',
  'inspector.before_after.layout_choice.pair': 'Two side by side',
  'inspector.before_after.layout_choice.cards': 'Photo cards in a row',
  'inspector.before_after.before_tag': 'Tag on before photo',
  'inspector.before_after.after_tag': 'Tag on after photo',
  'inspector.before_after.second_before_tag': 'Tag on second before photo',
  'inspector.before_after.second_after_tag': 'Tag on second after photo',
  'inspector.before_after.second_before_image': 'Second before photo',
  'inspector.before_after.second_after_image': 'Second after photo',
  'inspector.before_after.second_caption': 'Second caption',
  'inspector.before_after.frame': 'Frame',
  'inspector.before_after.frame_choice.none': 'None',
  'inspector.before_after.frame_choice.panel': 'White panel',
  'inspector.newsletter.image_side': 'Photos',
  'inspector.newsletter.image_side_choice.none': 'No photos',
  'inspector.newsletter.image_side_choice.left': 'Photos on the left',
  'inspector.newsletter.image_side_choice.right': 'Photos on the right',
  'inspector.newsletter.image': 'Photo',
  'inspector.newsletter.second_image': 'Second photo',
  'inspector.newsletter.collect_name': 'Ask for a name',
  'inspector.product_showcase.intro': 'A dark band with a big product picture, your words and a button, and a small card for one product on the picture. Until you pick a product, your newest one is shown.',
  'inspector.product_showcase.eyebrow': 'Small line above the heading',
  'inspector.product_showcase.body': 'Text',
  'inspector.product_showcase.image': 'Product scene',
  'inspector.product_showcase.layout': 'Layout',
  'inspector.product_showcase.layout_choice.words': 'Picture beside the words',
  'inspector.product_showcase.layout_choice.carousel': 'Picture with a product slider',
  'inspector.product_showcase.image_side': 'Picture side',
  'inspector.product_showcase.image_side_choice.left': 'Left',
  'inspector.product_showcase.image_side_choice.right': 'Right',
  'inspector.product_showcase.show_card': 'Show the product card',
  'inspector.product_showcase.card_position': 'Card position',
  'inspector.product_showcase.card_position_choice.top': 'Top',
  'inspector.product_showcase.card_position_choice.bottom': 'Bottom',
  'inspector.product_showcase.none': 'No product picked yet. Your newest product is shown until you pick one.',
  'inspector.product_showcase.show_category_pills': 'Show category buttons',
  'inspector.product_showcase.pill_style': 'Button style',
  'inspector.product_showcase.pill_style_choice.outline': 'Outlined',
  'inspector.product_showcase.pill_style_choice.filled': 'Filled',
  'inspector.product_showcase.pill_categories': 'Categories on the buttons',
  'inspector.product_showcase.pill_categories_hint': 'Tick up to {max}, in the order you want them. None ticked shows your first 6 categories.',
  'inspector.product_showcase.pill_no_categories': 'You have no categories yet. Add some on the Categories page and they show here.',
  'inspector.faq.image_side': 'Photo',
  'inspector.faq.image_side_choice.none': 'No photo',
  'inspector.faq.image_side_choice.left': 'Photo on the left',
  'inspector.faq.image_side_choice.right': 'Photo on the right',
  'inspector.faq.image_style': 'Photo style',
  'inspector.faq.image_style_choice.beside': 'Beside the questions',
  'inspector.faq.image_style_choice.background': 'Behind the questions',
  'inspector.faq.image': 'Photo',
  'inspector.timeline.layout': 'Layout',
  'inspector.timeline.layout_choice.vertical': 'Down the page',
  'inspector.timeline.layout_choice.horizontal': 'Across the page',
  'inspector.timeline.date': 'When',
  'inspector.timeline.title': 'What happened',
  'inspector.timeline.body': 'A few more words',
  'inspector.timeline.image': 'Picture',
  'inspector.bmi_calculator.intro': 'A short form that works out BMI from height and weight, in the shopper\'s browser. Nothing they type is saved or sent.',
  'inspector.bmi_calculator.units': 'Units',
  'inspector.bmi_calculator.units_choice.metric': 'cm and kg',
  'inspector.bmi_calculator.units_choice.imperial': 'ft, in and lb',
  'inspector.bmi_calculator.image': 'Photo',
  'inspector.bmi_calculator.image_side': 'Photo side',
  'inspector.bmi_calculator.image_side_choice.none': 'No photo',
  'inspector.bmi_calculator.image_side_choice.left': 'Left',
  'inspector.bmi_calculator.image_side_choice.right': 'Right',
  'inspector.bmi_calculator.message_under': 'Line for underweight',
  'inspector.bmi_calculator.message_healthy': 'Line for healthy weight',
  'inspector.bmi_calculator.message_over': 'Line for overweight',
  'inspector.bmi_calculator.message_obese': 'Line for obese',
  'inspector.bmi_calculator.note': 'Small print',
  'inspector.marquee.intro': 'Short benefits that scroll sideways without end. Click an item in the preview, or in the list, to change its words or icon.',
  'inspector.marquee.speed': 'Speed',
  'inspector.marquee.speed_choice.slow': 'Slow',
  'inspector.marquee.speed_choice.normal': 'Normal',
  'inspector.marquee.speed_choice.fast': 'Fast',
  'inspector.marquee.direction': 'Scrolls towards',
  'inspector.marquee.direction_choice.left': 'Left',
  'inspector.marquee.direction_choice.right': 'Right',
  'inspector.marquee.separator': 'Between items',
  'inspector.marquee.separator_choice.dot': 'Dot',
  'inspector.marquee.separator_choice.star': 'Star',
  'inspector.marquee.separator_choice.none': 'Nothing',
  'inspector.marquee.size': 'Text size',
  'inspector.marquee.size_choice.sm': 'Small',
  'inspector.marquee.size_choice.md': 'Medium',
  'inspector.marquee.size_choice.lg': 'Large',
  'inspector.marquee.uppercase': 'Capital letters',
  'inspector.marquee.pause_on_hover': 'Pause when pointed at',
  'inspector.marquee_item.text': 'Words',
  'inspector.marquee_item.icon': 'Icon',
  'inspector.marquee_item.no_icon': 'No icon',
};

export default sectionEditor;
